import {DisplayObject, Graphics, Sprite} from 'pixi.js';
import {Context} from '@gongxigames/framework/core/fragments/BuilderX/types';
import {Animation} from '@gongxigames/framework/core/fragments/display';
import {Image} from './Image';
import {Easing} from '@gongxigames/framework/core/fragments/tween/Easing';
import {Tween} from '@gongxigames/framework/core/fragments/tween/Tween';

export type TweenConfig = {
    chain?: TweenConfig[];
    to: number;
    time: number;
    delay?: number;
    easing?: string;
    yoyo?: boolean;
    repeat?: number;
};

export type ComponentProps = {
    children?: JSX.Element[]|JSX.Element;
    position?: [number?, number?];
    anchor?: [number?, number?];
    scale?: [number?, number?];
    rotation?: number;
    name?: string;
    height?: number;
    width?: number;
    alpha?: number;
    /**
     * @deprecated
     * Do not use! bugged studio flip logic. Use instead scale -1 and anchor
     */
    flipH?: boolean;
    /**
     * @deprecated
     * Do not use! bugged studio flip logic. Use instead scale -1 and anchor
     */
    flipV?: boolean;
    visible?: boolean;
    mask?: [number, number, number, number];
    reloadId?: string;
};

export type ComponentBuildOptions<T> = {
    context: Context;
    element?: T;
    forceKeepElement?: boolean;
};

const JSX_TYPE = Symbol('JSX_TYPE');

export abstract class Component<P extends ComponentProps, T = Sprite | Animation> {
    // Props for typescript JSX check
    public state!: Readonly<{}>;
    public refs!: { [key: string]: any; };
    public context!: any;
    public ignoreChildren = false;

    public abstract readonly jsxType: string;

    static propsFromJson(props: any): ComponentProps {
        const {width, height, alpha, mask, flipH, flipV, name, x, y, scaleX, scaleY, xAnchor, yAnchor, rotation, visible} = props;

        const normalizedProps: ComponentProps = {
            width, height, rotation, alpha, mask, flipH, flipV, visible, name, children: []
        };

        if (x || y) {
            normalizedProps.position = [x || 0, y || 0];
        }

        if (scaleX || scaleY) {
            normalizedProps.scale = [scaleX || undefined, scaleY || undefined];
        }

        if (xAnchor || yAnchor) {
            normalizedProps.anchor = [xAnchor || undefined, yAnchor || undefined];
        }

        return normalizedProps;
    }

    constructor(public props: P) {}

    public patchProps(props: P): this {
        this.props = {...this.props, ...props};

        return this;
    }

    public abstract clone(): Component<ComponentProps>;

    public getChildren(): Component<ComponentProps>[] {
        return (this.props.children as any[])?.slice() || [];
    }

    public propsToJson(): any {
        const {width, height, alpha, mask, flipH, flipV, name, position, scale, anchor, rotation, visible, children} = this.props;
        const result: Record<string, any> = {width, height, alpha, mask, flipH, flipV, name, rotation, visible};

        if (position) {
            result.x = position[0];
            result.y = position[1];
        }

        if (scale) {
            result.scaleX = scale[0];
            result.scaleY = scale[1];
        }

        if (anchor) {
            result.xAnchor = anchor[0];
            result.yAnchor = anchor[1];
        }

        const childrenData = (children || []) as any as Component<ComponentProps>[];

        result.children = childrenData.map(item => item.propsToJson());

        return result;
    }

    public build(options: ComponentBuildOptions<T>): T {
        const {context, forceKeepElement} = options;
        let element = options.element || this.getNewElement(context);

        if ((element as any)[JSX_TYPE] !== this.jsxType && !forceKeepElement) {
            element = this.getNewElement(context);
        }

        (element as any)[JSX_TYPE] = this.jsxType;

        const propsChildren = this.getChildrenToPixi();
        const children: Sprite[] = [];
        const container = (element as any).container || element;

        if (!this.ignoreChildren && container.children) {
            children.push(...container.children as Sprite[]);
            container.removeChildren();
        }

        this.updateBaseProps(context, container, element);

        if (!this.ignoreChildren) {
            propsChildren.forEach((item, i) => {
                const oldChild = children[i];
                let newChild;

                if (oldChild) {
                    newChild = item.build({context, element: oldChild});
                } else {
                    newChild = item.build({context});
                }

                container.addChild(newChild);
            });
        }

        this.updateProps(context, container, element);

        /* develblock:start */
        if (this.props.reloadId) {
            context.attachToReloadId(this.props.reloadId, container);
        }
        /* develblock:end */

        return element;
    }

    public findByNameOrSrc(name: string | number, prefix = ''): Component<ComponentProps> | undefined {
        const children = this.props.children as any as Image[];

        if (typeof name === 'number') {
            return children?.[name] as any;
        }

        if (name.includes('.')) {
            const keys = name.split('.');
            let item: Component<any, any> | undefined = this;

            for (const key of keys) {
                if (!item) {
                    return;
                }

                item = item.findByNameOrSrc(key, prefix);
            }

            return item;
        }

        name = prefix + name;

        return children.find(item => item.props.name === name || item.props.src === name);
    }

    public get type(): string {
        return this.jsxType;
    }

    public setState(): void {}
    public forceUpdate(): void {}
    public componentDidUpdate(prevState: Readonly<any>, snapshot?: any): void {}
    public componentWillUnmount(): void {}
    public componentDidMount(): void {}
    public render(element?: T): any {
        throw new Error();
    }

    protected abstract getConstructor(): new () => any;

    protected getNewElement(context: Context): T {
        return new (this.getConstructor())();
    }

    protected getChildrenToPixi(): Component<ComponentProps>[] {
        if (this.props.children && !Array.isArray(this.props.children)) {
            this.props.children = [this.props.children];
        }

        return this.props.children as any[];
    }

    protected updateBaseProps(context: Context, container: Sprite, element: T): void {
        const {props} = this;

        container.position.set(...(props.position || [0, 0]));
        container.rotation = props.rotation ?? 0;

        if (props.width !== undefined) {
            container.width = props.width;
        }

        if (props.height !== undefined) {
            container.height = props.height;
        }

        if (props.scale?.[0] !== undefined) {
            container.scale.x = props.scale?.[0];
        }

        if (props.scale?.[1] !== undefined) {
            container.scale.y = props.scale?.[1];
        }

        if (props.flipH) {
            container.scale.x *= -1;
            container.x += container.getLocalBounds().width;
        }

        if (props.flipV) {
            container.scale.y *= -1;
            container.y += container.getLocalBounds().height;
        }

        if (props.anchor && container instanceof Sprite) {
            container.anchor.set(...props.anchor);
        }

        if (props.mask) {
            const rect = new Graphics()
                .beginFill(0xffffff)
                .drawRect(props.mask[0], props.mask[1], props.mask[2], props.mask[3])
                .endFill();

            container.mask = rect;
            container.addChild(rect);
        }

        container.name = props.name!;
        container.alpha = props.alpha ?? 1;
        container.visible = props.visible ?? true;
    }

    protected updateProps(context: Context, container: Sprite, element: T): void {}

    protected getRepeat(repeat: any): number {
        if (repeat == null) {
            return 0;
        } else if (repeat === 'Infinity' || repeat === -1) {
            return Infinity;
        } else {
            return repeat;
        }
    }

    protected createTweenAnimation(displayObject: DisplayObject, animation: Animation, configs: TweenConfig[]): void {
        animation.addTweens(this.createTweens(displayObject, configs));
    }

    protected createTweens(displayObject: DisplayObject, configs: TweenConfig[]): Tween[] {
        const tweensArr: Tween[] = [];

        for (const tweenConfig of configs!) {
            tweensArr.push(this.createTween(displayObject, tweenConfig));
        }

        return tweensArr;
    }

    protected createTween(displayObject: DisplayObject, config: TweenConfig): Tween {
        const tween = new Tween(displayObject);

        if (config.chain) {
            const chain: Tween[] = [];

            for (const chainConfig of config.chain) {
                chain.push(this.createTween(displayObject, chainConfig));
            }
            tween.chain(chain);
        }

        tween.to(config.to, config.time)
            .delay(config.delay || 0)
            .easing(this.getEasing(config.easing))
            .yoyo(config.yoyo!)
            .repeat(this.getRepeat(config.repeat));

        return tween;
    }

    protected getEasing(easing?: string): Function {
        if (easing == null) {
            return Easing.Linear.None;
        }

        const keys = easing.split('.');

        return (Easing as any)[keys[0]][keys[1]];
    }
}
